/**
 * Design tokens for the Deview on-chain strategy UI.
 *
 * These values are lifted directly from the Claude-generated design system
 * (the dark-first "oa" theme with a paired light palette). They are the single
 * source of truth for colors/surfaces/status hues that the MUI theme and the
 * pages consume. MUI's standard palette maps a subset of these (see palette.ts);
 * the extras that MUI has no slot for (surface variants, tertiary text, status
 * bg/fg pairs, the mono font) are attached to the theme as `theme.tokens`.
 */

export type ColorMode = 'dark' | 'light';

/** A background/foreground pair used for status "pill" chips. */
export interface StatusColor {
  /** Subtle translucent chip background. */
  bg: string;
  /** Solid text/icon color. */
  fg: string;
}

export interface DesignTokens {
  mode: ColorMode;

  // Surfaces
  /** App background. */
  bg: string;
  /** Sidebar / darkest chrome. */
  sidebar: string;
  /** Primary surface (cards, tables, drawer paper). */
  surface: string;
  /** Raised / inset surface (chips, inputs, hovered rows). */
  surface2: string;
  /** Header bar background. */
  headerBg: string;

  // Borders
  border: string;
  border2: string;

  // Text
  /** High-emphasis text. */
  textPrimary: string;
  /** Medium-emphasis text. */
  textSecondary: string;
  /** Low-emphasis text / captions / disabled. */
  textTertiary: string;

  // Accent
  accent: string;
  /** Translucent accent fill (dimmed chips/buttons). */
  accentDim: string;
  /** Text color that sits on top of the solid accent. */
  onAccent: string;

  // Interaction
  rowHover: string;
  shadow: string;

  // Status hues (chip bg + fg)
  status: {
    active: StatusColor;
    inactive: StatusColor;
    draft: StatusColor;
    sent: StatusColor;
    pending: StatusColor;
    failed: StatusColor;
  };

  // Typography
  fontFamily: string;
  monoFontFamily: string;
}

const FONT_STACK =
  "'DM Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const MONO_STACK = "'DM Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

export const darkTokens: DesignTokens = {
  mode: 'dark',

  bg: '#030D1A',
  sidebar: '#020C16',
  surface: '#0B1929',
  surface2: '#112238',
  headerBg: '#0B1929',

  border: 'rgba(255,255,255,0.07)',
  border2: 'rgba(255,255,255,0.13)',

  textPrimary: '#DDE8F5',
  textSecondary: '#9BB1C7',
  textTertiary: '#6A8EAF',

  accent: '#00CFC9',
  accentDim: 'rgba(0,207,201,0.11)',
  onAccent: '#030D1A',

  rowHover: 'rgba(255,255,255,0.03)',
  shadow: '0 12px 28px -14px rgba(0,5,12,0.7)',

  status: {
    active: { bg: 'rgba(34,197,94,0.12)', fg: '#22C55E' },
    inactive: { bg: 'rgba(239,68,68,0.10)', fg: '#F87171' },
    draft: { bg: 'rgba(245,158,11,0.10)', fg: '#FCD34D' },
    sent: { bg: 'rgba(34,197,94,0.12)', fg: '#22C55E' },
    pending: { bg: 'rgba(245,158,11,0.10)', fg: '#FCD34D' },
    failed: { bg: 'rgba(239,68,68,0.10)', fg: '#F87171' },
  },

  fontFamily: FONT_STACK,
  monoFontFamily: MONO_STACK,
};

export const lightTokens: DesignTokens = {
  mode: 'light',

  bg: '#EEF3FA',
  sidebar: '#020C16',
  surface: '#FFFFFF',
  surface2: '#F4F8FC',
  headerBg: '#FFFFFF',

  border: 'rgba(0,0,0,0.08)',
  border2: 'rgba(0,0,0,0.14)',

  textPrimary: '#081828',
  textSecondary: '#2F4D68',
  textTertiary: '#4A6A88',

  accent: '#0097A7',
  accentDim: 'rgba(0,151,167,0.09)',
  onAccent: '#FFFFFF',

  rowHover: 'rgba(0,0,0,0.025)',
  shadow: '0 12px 28px -18px rgba(8,40,72,0.16)',

  status: {
    active: { bg: 'rgba(22,163,74,0.09)', fg: '#16A34A' },
    inactive: { bg: 'rgba(220,38,38,0.09)', fg: '#DC2626' },
    draft: { bg: 'rgba(217,119,6,0.09)', fg: '#D97706' },
    sent: { bg: 'rgba(22,163,74,0.09)', fg: '#16A34A' },
    pending: { bg: 'rgba(217,119,6,0.09)', fg: '#D97706' },
    failed: { bg: 'rgba(220,38,38,0.09)', fg: '#DC2626' },
  },

  fontFamily: FONT_STACK,
  monoFontFamily: MONO_STACK,
};

export const getTokens = (mode: ColorMode): DesignTokens =>
  mode === 'light' ? lightTokens : darkTokens;

/** Shared hero-banner gradient (branded, dark in both modes). */
export const HERO_GRADIENT =
  'linear-gradient(135deg, #051628 0%, #0A2540 55%, #062038 100%)';
